import { create } from 'zustand';
import { ShopItem } from '../types';

export const SHOP_CATEGORIES = [
  { id: 'ALL', label: 'Todos', badge: 'Catálogo' },
  { id: 'SKINS', label: 'Skins', badge: 'Trajes' },
  { id: 'GESTOS', label: 'Gestos', badge: 'Bailes' },
  { id: 'PICOS', label: 'Picos', badge: 'Herramientas' },
  { id: 'MOCHILAS', label: 'Mochilas', badge: 'Accesorios' },
  { id: 'LOTES', label: 'Lotes', badge: 'Packs' },
] as const;

export type ShopCategoryId = (typeof SHOP_CATEGORIES)[number]['id'] | string;

export type AppPage = 'landing' | 'shop' | 'cart' | 'profile' | 'recargas' | 'sorteos' | 'order';

function getInitialOrderCode(): string | null {
  if (typeof window === 'undefined' || !window.location) return null;
  const params = new URLSearchParams(window.location.search);
  return params.get('code') || null;
}

function getInitialPage(): AppPage {
  if (typeof window === 'undefined' || !window.location || typeof window.location.pathname !== 'string') {
    return 'landing';
  }
  const path = window.location.pathname.toLowerCase();
  if (path.includes('/tienda') || path.includes('tienda.html')) return 'shop';
  if (path.includes('/cart') || path.includes('cart.html') || path.includes('/carrito')) return 'cart';
  if (path.includes('/profile') || path.includes('profile.html') || path.includes('/perfil')) return 'profile';
  if (path.includes('/recargas') || path.includes('recargas.html')) return 'recargas';
  if (path.includes('/sorteos') || path.includes('sorteos.html')) return 'sorteos';
  if (path.includes('/order') || path.includes('order.html') || path.includes('/pedido')) return 'order';
  return 'landing';
}

export interface UIState {
  // Navigation & Active Page
  activePage: AppPage;
  navigateTo: (page: AppPage, customPath?: string) => void;

  // Search & Filters
  searchQuery: string;
  activeCategory: string; // Default: 'ALL'

  // Modals & Overlays
  isCartOpen: boolean;
  selectedItem: ShopItem | null;
  isDetailModalOpen: boolean;
  isCheckoutOpen: boolean;
  isOrderModalOpen: boolean;
  activeOrderCode: string | null;

  // Deposit Modal (Cargar G-Coins)
  isDepositModalOpen: boolean;
  depositInitialAmount: number;
  openDepositModal: (amount?: number) => void;
  closeDepositModal: () => void;

  // Raffle Modal (Sorteo Semanal)
  isRaffleModalOpen: boolean;
  openRaffleModal: () => void;
  closeRaffleModal: () => void;

  // Auth Modal
  isAuthModalOpen: boolean;
  authModalTab: 'login' | 'register';
  openAuthModal: (tab?: 'login' | 'register') => void;
  closeAuthModal: () => void;

  // Link Account Modal
  isLinkAccountModalOpen: boolean;
  openLinkAccountModal: () => void;
  closeLinkAccountModal: () => void;

  // Layout & Responsive Navigation
  isCompactHeader: boolean;
  isMobileMenuOpen: boolean;

  // Scroll Positions per Page
  scrollPositions: Record<string, number>;
  saveScrollPosition: (page: string, y: number) => void;

  // Actions
  setSearchQuery: (query: string) => void;
  clearSearchQuery: () => void;
  setActiveCategory: (category: string) => void;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
  openCheckout: () => void;
  closeCheckout: () => void;
  openOrderModal: (orderCode?: string) => void;
  closeOrderModal: () => void;
  setActiveOrderCode: (code: string | null) => void;
  openItemDetail: (item: ShopItem) => void;
  closeItemDetail: () => void;
  setCompactHeader: (compact: boolean) => void;
  toggleMobileMenu: () => void;
  closeMobileMenu: () => void;
  resetFilters: () => void;
}

export const useUIStore = create<UIState>()((set, get) => ({
  // Scroll Positions per Page
  scrollPositions: {},
  saveScrollPosition: (page: string, y: number) =>
    set((state) => ({
      scrollPositions: {
        ...state.scrollPositions,
        [page]: Math.max(0, Math.round(y)),
      },
    })),

  // Navigation
  activePage: getInitialPage(),
  navigateTo: (page: AppPage, customPath?: string) => {
    let extractedOrderCode: string | null = null;
    if (customPath) {
      try {
        const dummy = new URL(customPath, 'http://localhost');
        const qCode = dummy.searchParams.get('code');
        if (qCode) {
          extractedOrderCode = qCode.replace('#', '').trim().toUpperCase();
        } else {
          const parts = dummy.pathname.split('/');
          const last = parts[parts.length - 1];
          if (last && (last.startsWith('FN-') || last.startsWith('fn-'))) {
            extractedOrderCode = last.toUpperCase();
          }
        }
      } catch {}
    }

    const currentY =
      typeof window !== 'undefined' && typeof window.scrollY === 'number'
        ? window.scrollY
        : undefined;
    const prevPage = get().activePage;

    set((state) => ({
      scrollPositions: {
        ...state.scrollPositions,
        ...(currentY !== undefined
          ? { [prevPage]: Math.max(0, Math.round(currentY)) }
          : {}),
      },
      activePage: page,
      isMobileMenuOpen: false,
      activeOrderCode:
        extractedOrderCode !== null
          ? extractedOrderCode
          : page === 'order'
          ? state.activeOrderCode
          : null,
    }));
    if (typeof window !== 'undefined' && window.location && window.history) {
      const targetPath =
        customPath ||
        (page === 'landing'
          ? '/'
          : page === 'shop'
          ? '/tienda'
          : page === 'cart'
          ? '/cart.html'
          : page === 'profile'
          ? '/profile.html'
          : page === 'recargas'
          ? '/recargas'
          : page === 'sorteos'
          ? '/sorteos'
          : page === 'order'
          ? '/order.html'
          : '/');
      const currentFull =
        window.location.pathname + window.location.search + window.location.hash;
      if (currentFull !== targetPath) {
        window.history.pushState({ page }, '', targetPath);
      }
      if (targetPath.includes('#')) {
        window.dispatchEvent(new Event('hashchange'));
      }
    }
  },

  // Search
  searchQuery: '',
  activeCategory: 'ALL',
  setSearchQuery: (query: string) => set({ searchQuery: query }),
  clearSearchQuery: () => set({ searchQuery: '' }),

  // Category Filter
  setActiveCategory: (category: string) => {
    const normalized = (category || 'ALL').toUpperCase().trim();
    set({ activeCategory: normalized });
  },

  // Cart Drawer
  isCartOpen: false,
  openCart: () => set({ isCartOpen: true }),
  closeCart: () => set({ isCartOpen: false }),
  toggleCart: () => set((state) => ({ isCartOpen: !state.isCartOpen })),

  // Checkout & Order Modals
  isCheckoutOpen: false,
  isOrderModalOpen: false,
  activeOrderCode: getInitialOrderCode(),
  openCheckout: () => set({ isCheckoutOpen: true, isCartOpen: false }),
  closeCheckout: () => set({ isCheckoutOpen: false }),
  openOrderModal: (orderCode?: string) =>
    set((state) => ({
      isOrderModalOpen: true,
      activeOrderCode: orderCode ?? state.activeOrderCode,
      isCheckoutOpen: false,
      isCartOpen: false,
    })),
  closeOrderModal: () => set({ isOrderModalOpen: false }),
  setActiveOrderCode: (code: string | null) => set({ activeOrderCode: code }),

  // Item Detail Modal
  selectedItem: null,
  isDetailModalOpen: false,
  openItemDetail: (item: ShopItem) =>
    set({ selectedItem: item, isDetailModalOpen: true }),
  closeItemDetail: () =>
    set({ selectedItem: null, isDetailModalOpen: false }),

  // Deposit Modal
  isDepositModalOpen: false,
  depositInitialAmount: 2400,
  openDepositModal: (amount = 2400) =>
    set({ isDepositModalOpen: true, depositInitialAmount: amount }),
  closeDepositModal: () => set({ isDepositModalOpen: false }),

  // Raffle Modal
  isRaffleModalOpen: false,
  openRaffleModal: () => set({ isRaffleModalOpen: true }),
  closeRaffleModal: () => set({ isRaffleModalOpen: false }),

  // Auth Modal
  isAuthModalOpen: false,
  authModalTab: 'login',
  openAuthModal: (tab = 'login') =>
    set({ isAuthModalOpen: true, authModalTab: tab }),
  closeAuthModal: () => set({ isAuthModalOpen: false }),

  // Link Account Modal
  isLinkAccountModalOpen: false,
  openLinkAccountModal: () => set({ isLinkAccountModalOpen: true }),
  closeLinkAccountModal: () => set({ isLinkAccountModalOpen: false }),

  // Layout Controls
  isCompactHeader: false,
  isMobileMenuOpen: false,
  setCompactHeader: (compact: boolean) => set({ isCompactHeader: compact }),
  toggleMobileMenu: () =>
    set((state) => ({ isMobileMenuOpen: !state.isMobileMenuOpen })),
  closeMobileMenu: () => set({ isMobileMenuOpen: false }),

  // Reset
  resetFilters: () => set({ searchQuery: '', activeCategory: 'ALL' }),
}));

export default useUIStore;
